import { SetStateAction, useCallback, useEffect, useState } from 'react';
import { getInstance } from './db';

const db = getInstance('config');

export async function getPersistedValue<T>(id: string, defaultValue: T, setDefaultValue?: boolean): Promise<T> {
  const value = await (await db).readData(id);
  if (typeof value === 'string') return JSON.parse(value);
  if (setDefaultValue) await setPersistedValue(id, defaultValue);
  return defaultValue;
}

export async function setPersistedValue<T>(id: string, value: T | undefined) {
  if (typeof value !== 'undefined' && value !== null) {
    await (await db).writeData(id, JSON.stringify(value));
  } else {
    await (await db).deleteData(id);
  }
}

export function usePersistedValue<T>(id: string, defaultValue: T, setDefaultValue?: boolean): [T, (value: SetStateAction<T>) => Promise<void>, boolean] {
  const [pending, setPending] = useState(true);
  const [value, setValue] = useState<T>(defaultValue);

  const read = useCallback(async () => {
    const value = await (await db).readData(id);
    if (typeof value === 'string') {
      return JSON.parse(value);
    }
  }, [id]);

  const persist = useCallback(
    async (value: T) => {
      if (typeof value !== 'undefined' && value !== null) {
        await (await db).writeData(id, JSON.stringify(value));
      } else {
        await (await db).deleteData(id);
      }
    },
    [id]
  );

  useEffect(() => {
    read().then((value) => {
      if (value !== undefined) {
        setValue(value);
      } else {
        setValue(defaultValue);
        if (setDefaultValue) persist(defaultValue);
      }

      setPending(false);
    });
  }, [read, defaultValue, setDefaultValue, persist]);

  const set = useCallback(
    async (val: SetStateAction<T>) => {
      if (typeof val === 'function') {
        const update = val as (previous: T) => T;
        const previous = await read();
        const next = update(previous);
        await persist(next);
        setValue(next);
      } else {
        setValue(val);
        await persist(val);
      }
    },
    [read, persist]
  );

  return [value, set, pending];
}
